import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";

import { ConfirmDialog, Dialog, DialogFooter, DialogHeader, Dropdown, FormField } from "@/ds";
import { CampoDecimal } from "@/shared/components";
import { PreviaMensal } from "../previa-mensal";
import {
  CATEGORIA_LABEL,
  CATEGORIA_PLACEHOLDER,
  FASES_TITULO,
  MESES_LABEL,
  NOME_LABEL,
  PERCENTUAL_LABEL,
} from "./curva-dialog.constants";
import styles from "./curva-dialog.module.scss";
import type { CurvaDialogViewModel } from "./types-curva-dialog";

const RODAPE_DIALOGO = { footer: { className: styles.dialogoRodape } };
const PAINEL = { panel: { className: styles.painel } };

export function CurvaDialogView({
  formId,
  titulo,
  nome,
  categoria,
  fases,
  soma,
  uso,
  previa,
  nomeDaCurva,
  isSubmitting,
  submitError,
  salvarDesabilitado,
  salvarAviso,
  enviar,
  cancelar,
  descarte,
}: CurvaDialogViewModel) {
  return (
    <>
      <Dialog
        visible
        size="lg"
        className={styles.dialogo}
        contentClassName={styles.corpo}
        onHide={cancelar}
        locked={isSubmitting}
        focusOnShow={false}
        header={<DialogHeader title={titulo} />}
        pt={RODAPE_DIALOGO}
        footer={
          <DialogFooter
            className={styles.rodape}
            confirmLabel="Salvar"
            confirmButtonProps={{
              disabled: salvarDesabilitado,
              tooltip: salvarAviso ?? undefined,
            }}
            formId={formId}
            loading={isSubmitting}
            locked={isSubmitting}
            onCancel={cancelar}
            testId="curva"
          />
        }
        data-testid="curva-dialog"
      >
        <form id={formId} className={styles.form} onSubmit={enviar} noValidate>
          <div className={styles.identificacao}>
            <FormField.Root required error={nome.erro}>
              <FormField.Label>{NOME_LABEL}</FormField.Label>
              <FormField.Control>
                <InputText
                  value={nome.valor}
                  maxLength={nome.max}
                  onChange={(evento) => nome.digitar(evento.target.value)}
                  disabled={isSubmitting}
                  data-testid="curva-nome"
                />
              </FormField.Control>
              <FormField.Error />
            </FormField.Root>
            <FormField.Root required error={categoria.erro} disabled={categoria.desabilitado}>
              <FormField.Label>{CATEGORIA_LABEL}</FormField.Label>
              <FormField.Control>
                <Dropdown
                  appendTo="self"
                  pt={PAINEL}
                  value={categoria.valor}
                  options={categoria.opcoes}
                  placeholder={CATEGORIA_PLACEHOLDER}
                  showClear={false}
                  onChange={(evento) => categoria.escolher(evento.value)}
                  data-testid="curva-categoria"
                />
              </FormField.Control>
              <FormField.Error />
            </FormField.Root>
          </div>

          <h3 className={styles.fasesTitulo}>{FASES_TITULO}</h3>
          <div className={styles.fases}>
            {fases.map((fase) => (
              <section
                key={fase.titulo}
                className={styles.fase}
                role="group"
                aria-label={fase.titulo}
              >
                <h4 className={styles.faseTitulo}>{fase.titulo}</h4>
                <div className={styles.campos}>
                  <CampoDecimal
                    label={PERCENTUAL_LABEL}
                    casas={2}
                    value={fase.percentual.valor}
                    onChange={fase.percentual.digitar}
                    error={fase.percentual.erro}
                    disabled={isSubmitting}
                    testId={fase.percentual.testId}
                  />
                  {fase.meses ? (
                    <CampoDecimal
                      label={MESES_LABEL}
                      casas={0}
                      value={fase.meses.valor}
                      onChange={fase.meses.digitar}
                      error={fase.meses.erro}
                      disabled={isSubmitting}
                      testId={fase.meses.testId}
                    />
                  ) : null}
                </div>
                {fase.apoio ? <p className={styles.apoio}>{fase.apoio}</p> : null}
              </section>
            ))}
          </div>

          <div className={styles.soma}>
            <Tag
              value={soma.texto}
              severity={soma.valida ? "success" : "danger"}
              data-testid="curva-soma"
            />
            <p
              className={soma.valida ? styles.apoio : styles.erroSoma}
              role={soma.valida ? undefined : "alert"}
              data-testid="curva-soma-mensagem"
            >
              {soma.mensagem}
            </p>
          </div>

          {uso ? (
            <Message
              className="p-message-block"
              severity="info"
              text={uso}
              data-testid="curva-aviso-uso"
            />
          ) : null}

          <PreviaMensal nome={nomeDaCurva} fases={previa} id="curva-previa" />

          {submitError ? (
            <Message
              className="p-message-block"
              severity="error"
              text={submitError}
              data-testid="curva-submit-error"
            />
          ) : null}
        </form>
      </Dialog>
      {descarte ? <ConfirmDialog {...descarte} /> : null}
    </>
  );
}
